<template>
  <v-data-table
    calculate-widths
    class="tender-table lots"
    :class="{ 'in_creating': in_creating, 'is-default-cursor': disableOpenOffersForLot }"
    :custom-sort="(...args) => customSortSlice(group, args)"
    disable-pagination
    fixed-header
    :headers="lots_headers"
    hide-default-footer
    item-key="lotId"
    :items="group.items"
    :no-data-text="$t('Tenders.NoData')">
    <!-- Заголовок лота -->
    <template #[`item.title`]="{ item }">
      <component
        :is="(disableOpenOffersForLot || !item.bidsCount)
          ? 'div'
          : 'router-link'"
        :data-qa="(disableOpenOffersForLot || !item.bidsCount) 
          ? `lot-key-${item.lotId}` 
          : `lot-key-${item.lotId}-link`"
        v-bind="(disableOpenOffersForLot || !item.bidsCount) 
          ? {} 
          : { to: { name: 'offersForLot', params: { tenderId: tender.tenderId, lotId: item.lotId, org: $route.params.org } } }">
        #{{ item.lotNumber || item.lotId }}
        {{ item.title || item.lotSubject }}
      </component>
      <div
        v-if="item.analogs && item.analogs.length"
        style="max-height: 150px; overflow: auto;">
        <div 
          v-for="analog in item.analogs"
          :key="analog.analogId"
          class="text-left grey--text h8-tex mt-1">
          {{ analog.title }}
        </div>
      </div>
    </template>
    
    <!-- Описание лота -->
    <template #[`item.description`]="{ item }">
      {{ item.description || '–' }}
    </template>
    
    <!-- Начальная стоимость -->
    <template #[`item.initialAmount`]="{ item }">
      <span
        v-if="item.initialAmount || item.startAmount"
        data-qa="initialAmount">
        {{ $_tendersSettings.priceRoundingDigit
          ? $formatPrice(item.initialAmount || item.startAmount || 0, $_tendersSettings.priceRoundingDigit)
          : formSum( item.initialAmount || item.startAmount || 0 ) }}
        <template v-if="bidding_type.tenderingForPercentage">%</template>
        <template v-else>
          {{ tender.currency ? tender.currency.title : tender.tenderCurrency }}
        </template>
      </span>
      <span v-else>–</span>
      <CurrencyConversionTooltip
        v-if="item.initialAmount || item.startAmount"
        :currency="tender.currency ? tender.currency.title : tender.tenderCurrency"
        :current-id="tender.currency ? tender.currency.code : null"
        :nbu-currency="tender.nbuCurrency"
        :price="item.initialAmount || item.startAmount || 0"/>
    </template>
    
    <!-- Мінімальна стоимость -->
    <template #[`item.bidPriceLimit`]="{ item }">
      <span
        data-qa="bidPriceLimit">
        {{ $_tendersSettings.priceRoundingDigit
          ? $formatPrice(item.bidPriceLimit || 0, $_tendersSettings.priceRoundingDigit)
          : formSum( item.bidPriceLimit || 0 ) }}
        {{ tender.currency ? tender.currency.title : tender.tenderCurrency }}
      </span>
      <CurrencyConversionTooltip
        v-if="item.bidPriceLimit"
        :currency="tender.currency ? tender.currency.title : tender.tenderCurrency"
        :current-id="tender.currency ? tender.currency.code : null"
        :nbu-currency="tender.nbuCurrency"
        :price="item.bidPriceLimit || 0"/>
    </template>

    <!-- Скидки -->
    <template #[`item.discounts`]="{ item }">
      <TenderDiscounts
        :options="{currency: tender.currency.title, measure: item.unit.title}"
        readonly
        :value="{startAmount: item.initialAmount, discounts: item.discounts}"/>
    </template>
    
    <!-- Количество -->
    <template #[`item.quantity`]="{ item }">
      <div data-qa="quantity">
        {{ formSum(item.quantity,null,3) }}
        <span
          v-if="item.quantity"
          class="h7-text pl-1"
          :title="getMeasure(item)">
          {{ getMeasure(item) }}
        </span>
      </div>
    </template>
    
    <!-- Единица измерения -->
    <template #[`item.unit`]="{ item }">
      {{ (item.unit || {}).title }}
    </template>
    
    <!-- Шаг торгов -->
    <template #[`item.bidStep`]="{ item }">
      <span
        v-if=" item.bidStep "
        data-qa="bidStep"> {{ formSum(item.bidStep) }}  </span>
      <span
        v-else-if="item.minimalStepPercent "
        data-qa="bidStep"> {{ item.minimalStepPercent }} </span>
      <span
        v-else
        data-qa="bidStep">–</span>
      <span v-if="bidding_type.tenderingForPercentage">%</span>
      <span v-else-if="item.bidStep || item.minimalStepPercent">
        {{ tender.fixedStep ? tender.currency ? tender.currency.title : tender.tenderCurrency : '%' }} </span>
    </template>
    
    <!-- Общая сумма -->
    <template #[`item.summ`]="{ item }">
      <span
        v-if="(item.initialAmount || item.startAmount || 0 ) * item.quantity "
        data-qa="summ">
        {{ formSum((item.initialAmount || item.startAmount || 0 ) * item.quantity) }}
        {{ tender.currency ? tender.currency.title : tender.tenderCurrency }}
      </span>
      <span
        v-else
        data-qa="summ">–</span>
      <CurrencyConversionTooltip
        v-if="item.initialAmount || item.startAmount"
        :currency="tender.currency ? tender.currency.title : tender.tenderCurrency"
        :current-id="tender.currency ? tender.currency.code : null"
        :nbu-currency="tender.nbuCurrency"
        :price="(item.initialAmount || item.startAmount || 0 ) * item.quantity"/>
    </template>
    
    <!-- Сумма ставки -->
    <template #[`item.bidSum`]="{ item }">
      <div data-qa="bidSum">
        <span v-if="item.bidSum">
          {{ $_tendersSettings.priceRoundingDigit
            ? $formatPrice(item.bidSum, $_tendersSettings.priceRoundingDigit)
            : formSum(item.bidSum) }}
          <template v-if="bidding_type.tenderingForPercentage">%</template>
          <template v-else>
            {{ tender.currency ? tender.currency.title : tender.tenderCurrency }}
          </template>
        </span>
        <span v-else>–</span>
      </div>
    </template>
    
    <!-- Сумма -->
    <template #[`item.sum`]="{ item }">
      <div data-qa="sum">
        <span v-if="item.sum">
          {{ $_tendersSettings.priceRoundingDigit
            ? $formatPrice(item.sum, $_tendersSettings.priceRoundingDigit)
            : formSum(item.sum) }}
          <template v-if="bidding_type.tenderingForPercentage">%</template>
          <template v-else>
            {{ tender.currency ? tender.currency.title : tender.tenderCurrency }}
          </template>
        </span>
        <span v-else>–</span>
      </div>
    </template>
    
    <!-- Экономия -->
    <template #[`item.economy`]="{ item }">
      <div data-qa="economy">
        <span
          v-if="item.economy"
          :class="{'red--text': item.economy < 0}">
          {{ $_tendersSettings.priceRoundingDigit
            ? $formatPrice(item.economy, $_tendersSettings.priceRoundingDigit)
            : formSum(item.economy) }}
          <template v-if="bidding_type.tenderingForPercentage">%</template>
          <template v-else>
            {{ tender.currency ? tender.currency.title : tender.tenderCurrency }}
          </template>
        </span>
        <span v-else>–</span>
      </div>
    </template>
    
    <!-- Экономия в процентах -->
    <template #[`item.economyPercent`]="{ item }">
      <div data-qa="economyPercent">
        <span
          v-if="item.economyPercent !== null && item.economyPercent !== undefined"
          :class="{'red--text': item.economyPercent < 0}">
          {{ formSum(item.economyPercent, null, 2) }}%
        </span>
        <span v-else>–</span>
      </div>
    </template>
    
    <!-- Количество ставок -->
    <template #[`item.bidsCount`]="{ item }">
      <router-link
        v-if="!disableOpenOffersForLot && item.bidsCount"
        :data-qa="`lot-bids-${item.lotId}`"
        :to="{ name: 'offersForLot', params: { tenderId: tender.tenderId, lotId: item.lotId, org: $route.params.org } }">
        {{ item.bidsCount }}
      </router-link>
      <span
        v-else
        :data-qa="`lot-bids-${item.lotId}`">
        {{ item.bidsCount || 0 }}
      </span>
    </template>
    
    <!-- Участники -->
    <template #[`item.bidders`]="{ item }">
      <sTooltip
        :content="$t('Tenders.NumberOfSuppliersByLot')">
        <IconCount
          :count="item.bidsCount"
          icon="person-thin"
          :icon-color="!item.bidsCount ? 'grey': ''"
          :text-class="{ 'grey--text': !item.bidsCount }"/>
      </sTooltip>
    </template>
    
    <!-- Адрес -->
    <template #[`item.addressId`]="{ item }">
      <sTooltip
        v-if="item.addressId">
        <span> {{ $_getAddressTitle(item.addressId) }} </span>
        <div
          slot="content"
          class="tooltip-item text-left pa-2 black--text h8-text">
          {{ $_geFulltAddress(item.addressId) }}
        </div>
      </sTooltip>
      <span v-else> - </span>
    </template>
    
    <!-- Дополнительные поля тендера -->
    <template
      v-if="tender.additional_fields"
      #[`item.additional_fields`]="{ item }">
      <span 
        v-for="(field, index) in $_getAdditionalFieldsForLot(item)"
        :key="field.id"
        :data-qa="`additional-field-${field.id}`">
        {{ $_formatAdditionField(field.value, field.type, index, $_getAdditionalFieldsForLot(item).length) }}
      </span>
    </template>
    <!-- Дополнительные поля -->
    <template
      v-for="(field) in additional_fields"
      #[`item.AdditionalFields.${field.id}`]="{ item, index }">
      <template
        v-if="field.typeId === 'URL'">
        <a
          :key="field.id + '-' + item.lotId"
          :href="field.fieldValues[index].value"
          rel="nofollow"
          target="_blank">{{ field.fieldValues[index].value }}</a>
      </template>
      <template v-else>
        <div
          :key="field.id + '-' + item.lotId">
          {{ formatField(field, item.lotId) }}
        </div>
      </template>
    </template>
  </v-data-table>
</template>

<script>
import TenderDiscounts from 'components/tenders/TenderDiscounts.vue'
import CurrencyConversionTooltip from 'components/common/elements/CurrencyConversionTooltip.vue'
import IconCount from 'components/common/elements/IconCount.vue'
import lotsTableMixin from 'mixins/lotsTableMixin.js'

export default {
  name: 'LotsTableBase',
  components: {
    TenderDiscounts,
    CurrencyConversionTooltip,
    IconCount
  },
  mixins: [lotsTableMixin]
}
</script>